import {
  Row,
  Col,
  Button,
  message,
  Table,
  Tag,
  Input,
  InputNumber,
  Modal,
  Descriptions,
  Alert,
} from "antd";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  approvePurchaseCodeAmendment,
  cancelPurchaseCodeAmendment,
  getPurchaseCodeAmendmentById,
  rejectPurchaseCodeAmendment,
  resetAmendmentStatus,
} from "store/purchase-codes/purchaseCodesSlice";
import { routes } from "common/routes";
import { getPersistedUser } from "services/PersistService/PersistUserService";

const MESSAGE_KEY = "purchase_code_amendment_detail";

const statusColorMap: Record<string, string> = {
  PENDING: "gold",
  APPROVED: "green",
  REJECTED: "red",
  CANCELLED: "default",
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const PurchaseCodeAmendmentDetailPage = () => {
  useDocumentTitle("Purchase Code Request Detail");
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentAmendment, amendmentStatus, amendmentError } = useAppSelector(
    (state) => state.purchaseCodes
  );
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelRemarks, setCancelRemarks] = useState("");
  // Guards against a leftover "success" status from a submit on another page
  const [actionTaken, setActionTaken] = useState(false);

  // Checker-editable fields (HSN + GST) — optional for the maker, mandatory to approve
  const [hsnInput, setHsnInput] = useState<string>("");
  const [cgstInput, setCgstInput] = useState<number | null>(null);
  const [sgstInput, setSgstInput] = useState<number | null>(null);
  const [igstInput, setIgstInput] = useState<number | null>(null);

  const currentUserId = Number(getPersistedUser()?.user?.id);
  const isMaker = currentAmendment?.requestedBy === currentUserId;
  const isPending = currentAmendment?.status === "PENDING";
  const isDeleteRequest = currentAmendment?.requestType === "DELETE";
  const isChecker = isPending && !isMaker;
  const canEditTaxFields = isChecker && !isDeleteRequest;

  useEffect(() => {
    dispatch(resetAmendmentStatus());
    if (id) {
      dispatch(getPurchaseCodeAmendmentById(Number(id)));
    }
    return () => {
      dispatch(resetAmendmentStatus());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (!currentAmendment) return;
    const requested = currentAmendment.requestedPayload ?? {};
    const requestedMetadata = (requested.metadata ?? {}) as Record<string, unknown>;
    // GST lives under metadata.gst; fall back to flat metadata fields for older requests
    const requestedGst = {
      ...requestedMetadata,
      ...((requestedMetadata.gst ?? {}) as Record<string, unknown>),
    };
    setHsnInput(requested.hsn != null ? String(requested.hsn) : "");
    setCgstInput(requestedGst.cgst != null ? Number(requestedGst.cgst) : null);
    setSgstInput(requestedGst.sgst != null ? Number(requestedGst.sgst) : null);
    setIgstInput(requestedGst.igst != null ? Number(requestedGst.igst) : null);
  }, [currentAmendment]);

  useEffect(() => {
    if (amendmentStatus === "loading") {
      message.loading({ content: "Loading", key: MESSAGE_KEY });
    } else if (amendmentStatus === "saving") {
      message.loading({ content: "Processing", key: MESSAGE_KEY });
    } else if (amendmentStatus === "success" && actionTaken) {
      message.success({ content: "Request updated", key: MESSAGE_KEY });
      navigate(`/${routes.PO.PURCHASE_CODE_AMENDMENTS}`);
    } else if (amendmentStatus === "error" && amendmentError) {
      message.error({ content: amendmentError, key: MESSAGE_KEY });
    } else {
      message.destroy(MESSAGE_KEY);
    }
  }, [amendmentStatus, amendmentError, navigate, actionTaken]);

  const handleApprove = () => {
    if (!currentAmendment) return;

    if (isDeleteRequest) {
      setActionTaken(true);
      dispatch(approvePurchaseCodeAmendment({ id: currentAmendment.id }));
      return;
    }

    if (!hsnInput.trim() || cgstInput == null || sgstInput == null || igstInput == null) {
      message.error({
        content: "HSN, CGST, SGST and IGST are required to approve this request",
        key: MESSAGE_KEY,
      });
      return;
    }

    const requested = currentAmendment.requestedPayload ?? {};
    const {
      cgst: _cgst,
      sgst: _sgst,
      igst: _igst,
      ...requestedMetadata
    } = (requested.metadata ?? {}) as Record<string, unknown>;
    setActionTaken(true);
    dispatch(
      approvePurchaseCodeAmendment({
        id: currentAmendment.id,
        payload: {
          ...requested,
          hsn: hsnInput.trim(),
          metadata: {
            ...requestedMetadata,
            gst: {
              cgst: cgstInput,
              sgst: sgstInput,
              igst: igstInput,
            },
          },
        },
      })
    );
  };

  const handleReject = () => {
    if (!currentAmendment || !rejectRemarks.trim()) {
      message.error("Remarks are required to reject");
      return;
    }
    setActionTaken(true);
    dispatch(rejectPurchaseCodeAmendment({ id: currentAmendment.id, remarks: rejectRemarks.trim() }));
    setRejectModalOpen(false);
  };

  const handleCancel = () => {
    if (!currentAmendment) return;
    setActionTaken(true);
    dispatch(cancelPurchaseCodeAmendment({
      id: currentAmendment.id,
      remarks: cancelRemarks.trim() || undefined,
    }));
    setCancelModalOpen(false);
  };

  const payload = currentAmendment?.requestedPayload ?? {};
  const metadata = (payload.metadata ?? {}) as Record<string, unknown>;
  // GST lives under metadata.gst; fall back to flat metadata fields for older requests
  const gst = { ...metadata, ...((metadata.gst ?? {}) as Record<string, unknown>) };
  const imageUrl = payload.image ? String(payload.image) : "";

  const changeColumns = [
    { title: "Field", dataIndex: "field", key: "field" },
    {
      title: "Old Value",
      dataIndex: "oldValue",
      key: "oldValue",
      render: (value: unknown) => formatValue(value),
    },
    {
      title: "New Value",
      dataIndex: "newValue",
      key: "newValue",
      render: (value: unknown) => formatValue(value),
    },
  ];

  const changes = currentAmendment?.changes ?? [];

  return (
    <>
      <Row>
        <Col xs={24}>
          <AppPageHeader title={`Purchase Code Request #${id}`} />
        </Col>
      </Row>

      {currentAmendment && (
        <>
          <Descriptions bordered size="small" column={2} style={{ marginBottom: "1.5rem" }}>
            <Descriptions.Item label="Request ID">{currentAmendment.id}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={statusColorMap[currentAmendment.status] ?? "default"}>
                {currentAmendment.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Request Type">{currentAmendment.requestType}</Descriptions.Item>
            <Descriptions.Item label="Entity ID">{currentAmendment.entityId ?? "-"}</Descriptions.Item>
            <Descriptions.Item label="Requested By">{currentAmendment.requestedBy}</Descriptions.Item>
            <Descriptions.Item label="Requested At">
              {new Date(currentAmendment.requestedAt).toLocaleString()}
            </Descriptions.Item>
            {currentAmendment.metadata?.remarks && (
              <Descriptions.Item label="Review Remarks" span={2}>
                {currentAmendment.metadata.remarks}
              </Descriptions.Item>
            )}
          </Descriptions>

          {isPending && isMaker && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: "1rem" }}
              message="You submitted this request. You can cancel it while it is pending."
            />
          )}

          {isPending && !isMaker && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: "1rem" }}
              message={
                isDeleteRequest
                  ? "Approving this request will delete the purchase code. Review and approve or reject."
                  : "Review the requested purchase code below and approve or reject this request. HSN and GST fields are editable and required for approval."
              }
            />
          )}

          {changes.length > 0 && (
            <Table
              title={() => <strong>Requested Changes</strong>}
              columns={changeColumns}
              dataSource={changes.map((change, index) => ({
                ...change,
                key: `${change.field}-${index}`,
              }))}
              pagination={false}
              size="small"
              bordered
              style={{ marginBottom: "1.5rem" }}
            />
          )}

          <Descriptions
            title={isDeleteRequest ? "Purchase Code to Delete" : "Requested Purchase Code"}
            bordered
            size="small"
            column={2}
            style={{ marginBottom: "1.5rem" }}
          >
            <Descriptions.Item label="SKU Code">{formatValue(payload.skuCode)}</Descriptions.Item>
            <Descriptions.Item label="Name">{formatValue(payload.name)}</Descriptions.Item>
            <Descriptions.Item label="Category">{formatValue(payload.category)}</Descriptions.Item>
            <Descriptions.Item label="Unit of Measurement">
              {formatValue(payload.unit_of_measurement)}
            </Descriptions.Item>
            <Descriptions.Item label="HSN">
              {canEditTaxFields ? (
                <Input
                  size="small"
                  style={{ maxWidth: 180 }}
                  value={hsnInput}
                  onChange={(e) => setHsnInput(e.target.value)}
                  placeholder="Required to approve"
                />
              ) : (
                formatValue(payload.hsn)
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Company Tag">{formatValue(metadata.companyTag)}</Descriptions.Item>
            <Descriptions.Item label="CGST (%)">
              {canEditTaxFields ? (
                <InputNumber
                  size="small"
                  min={0}
                  max={100}
                  step={0.01}
                  precision={2}
                  value={cgstInput}
                  onChange={(value) => setCgstInput(value)}
                  placeholder="Required"
                />
              ) : (
                formatValue(gst.cgst)
              )}
            </Descriptions.Item>
            <Descriptions.Item label="SGST (%)">
              {canEditTaxFields ? (
                <InputNumber
                  size="small"
                  min={0}
                  max={100}
                  step={0.01}
                  precision={2}
                  value={sgstInput}
                  onChange={(value) => setSgstInput(value)}
                  placeholder="Required"
                />
              ) : (
                formatValue(gst.sgst)
              )}
            </Descriptions.Item>
            <Descriptions.Item label="IGST (%)">
              {canEditTaxFields ? (
                <InputNumber
                  size="small"
                  min={0}
                  max={100}
                  step={0.01}
                  precision={2}
                  value={igstInput}
                  onChange={(value) => setIgstInput(value)}
                  placeholder="Required"
                />
              ) : (
                formatValue(gst.igst)
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Image">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={formatValue(payload.name)}
                  style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 4 }}
                />
              ) : (
                "-"
              )}
            </Descriptions.Item>
          </Descriptions>

          <Row gutter={12}>
            {isPending && !isMaker && (
              <>
                <Col>
                  <Button type="primary" onClick={handleApprove} loading={amendmentStatus === "saving"}>
                    Approve
                  </Button>
                </Col>
                <Col>
                  <Button danger onClick={() => setRejectModalOpen(true)} loading={amendmentStatus === "saving"}>
                    Reject
                  </Button>
                </Col>
              </>
            )}
            {isPending && isMaker && (
              <Col>
                <Button danger onClick={() => setCancelModalOpen(true)} loading={amendmentStatus === "saving"}>
                  Cancel Request
                </Button>
              </Col>
            )}
            <Col>
              <Button onClick={() => navigate(`/${routes.PO.PURCHASE_CODE_AMENDMENTS}`)}>
                Back to Requests
              </Button>
            </Col>
          </Row>
        </>
      )}

      <Modal
        title="Reject Request"
        open={rejectModalOpen}
        onCancel={() => setRejectModalOpen(false)}
        onOk={handleReject}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setRejectModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              danger
              onClick={handleReject}
              loading={amendmentStatus === "saving"}
              disabled={!rejectRemarks.trim()}
            >
              Reject
            </Button>
          </div>
        }
      >
        <p>Remarks are required to reject this request.</p>
        <Input.TextArea
          rows={3}
          value={rejectRemarks}
          onChange={(e) => setRejectRemarks(e.target.value)}
          placeholder="Enter rejection remarks"
        />
      </Modal>

      <Modal
        title="Cancel Request"
        open={cancelModalOpen}
        onCancel={() => setCancelModalOpen(false)}
        onOk={handleCancel}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setCancelModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              danger
              onClick={handleCancel}
              loading={amendmentStatus === "saving"}
            >
              Cancel Request
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to cancel this request?</p>
        <Input.TextArea
          rows={3}
          value={cancelRemarks}
          onChange={(e) => setCancelRemarks(e.target.value)}
          placeholder="Optional remarks"
        />
      </Modal>
    </>
  );
};

export default PurchaseCodeAmendmentDetailPage;
